import React, {useEffect, useState} from 'react';
import { format } from 'date-fns';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { SegmentedControl } from '@mantine/core';
import ChartContainer from '@/components/common/shared/Charts/chartContainer/ChartContainer';
import ChartHeader from '@/components/common/shared/Charts/chartHeader/ChartHeader';
import CustomTick from '@/components/common/shared/Charts/charts/CustomTick';
import { useAutoRange } from '@/components/common/shared/Charts/useAutoRange';
import { useFetchChartData } from '@/components/common/shared/Charts/useFetchChartData';
import { PricesAndChartsService } from '@/services/PricesAndChartsService';
import { REGULAR_RANGE, TICK_GRANULARITY } from '@/utils/chartsConfig';
import {formatValue} from "@/utils/formatters";
import { getUnits } from '@/utils/chartsConfig';

type SpotMonthlyChartProps = {
  contract: any;
  height?: number;
  isModalView?: boolean;
};

type Granularity = 'daily' | '60' | '15';

const INTRADAY_RANGES = ['5D', '1M'] as const;
type IntradayRange = (typeof INTRADAY_RANGES)[number];

// Only the TGe24 SPOT chart has intraday (hourly / 15-min) TGE RDN quotations.
const INTRADAY_ENABLED_CONTRACT = 'TGe24';

const toYmd = (d: Date) => format(d, 'yyyy-MM-dd');

// RDN delivery date is session + 1, so include tomorrow's delivery scraped today.
const buildIntradayRange = (range: IntradayRange) => {
  const end = new Date();
  end.setDate(end.getDate() + 1);
  const start = new Date(end);
  start.setDate(start.getDate() - (range === '1M' ? 31 : 5));
  return { startDate: toYmd(start), endDate: toYmd(end) };
};

const SpotMonthlyChart = ({
  contract,
  height = 200,
  isModalView = true,
}: SpotMonthlyChartProps) => {
  const adjustedHeight = isModalView ? 600 : height;
  const [selectedRange, setSelectedRange] = useState(REGULAR_RANGE[2]);
  const { tooltip: tooltipUnit, axis: axisUnit } = getUnits(contract);

  const intradayEnabled = contract === INTRADAY_ENABLED_CONTRACT;
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const [intradayRange, setIntradayRange] = useState<IntradayRange>('5D');
  const [intradayData, setIntradayData] = useState<any[]>([]);

  const { data, lowerLimit, upperLimit, availableRanges } = useFetchChartData(
    () => PricesAndChartsService.fetchByContractAndRangeOrDate(contract, selectedRange),
    [selectedRange, contract],
    () => PricesAndChartsService.fetchAvailableRanges(contract)
  );

  useAutoRange(data, availableRanges, selectedRange, setSelectedRange);

  useEffect(() => {
    if (!intradayEnabled || granularity === 'daily') {
      return;
    }
    const { startDate, endDate } = buildIntradayRange(intradayRange);
    PricesAndChartsService.fetchSpotIntraday(
      granularity === '60' ? 60 : 15,
      startDate,
      endDate
    )
      .then((points) =>
        setIntradayData(
          points.map((p) => ({
            key: `${p.date} ${p.timeLabel}`,
            value: p.price,
            volume: p.volume,
            date: p.date,
            timeLabel: p.timeLabel,
          }))
        )
      )
      .catch(() => setIntradayData([]));
  }, [intradayEnabled, granularity, intradayRange, contract]);

  const isIntraday = intradayEnabled && granularity !== 'daily';

  const tickEvery =
    TICK_GRANULARITY.SpotIntradayChart?.[intradayRange] ?? 24;

  const formatIntradayTick = (key: string) => {
    const [datePart, timePart] = String(key).split(' ');
    const d = new Date(datePart);
    return `${format(d, 'dd.MM')} ${timePart}`;
  };

  return (
    <div style={{ width: '100%', pointerEvents: isModalView ? 'auto' : 'none' }}>
      {intradayEnabled && (
        <SegmentedControl
          size="xs"
          value={granularity}
          onChange={(value) => setGranularity(value as Granularity)}
          data={[
            { label: 'Dzienny', value: 'daily' },
            { label: 'Godzinowy', value: '60' },
            { label: '15 min', value: '15' },
          ]}
          style={{ marginBottom: 8 }}
        />
      )}

      {isIntraday ? (
        <>
          <ChartHeader
            ranges={[...INTRADAY_RANGES]}
            selectedRange={intradayRange}
            onRangeChange={(range) => setIntradayRange(range as IntradayRange)}
          />
          <ChartContainer height={adjustedHeight}>
            <BarChart data={intradayData} height={adjustedHeight}>
              {isModalView && (
                <CartesianGrid
                  stroke="#aaa"
                  strokeDasharray="3 3"
                  vertical={true}
                  horizontal={true}
                  strokeOpacity={0.9}
                />
              )}
              <XAxis
                dataKey="key"
                tick={{ fontSize: 10, fill: '#666' }}
                tickFormatter={formatIntradayTick}
                interval={Math.max(0, tickEvery - 1)}
                tickMargin={10}
                angle={-45}
                textAnchor="end"
                axisLine={true}
                tickLine={false}
                height={60}
              />
              <YAxis
                width={80}
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => formatValue(value)}
              />
              <Tooltip
                formatter={(value: any) => [`${formatValue(value)} ${tooltipUnit}`, 'Cena']}
                labelFormatter={(label) => {
                  const [datePart, timePart] = String(label).split(' ');
                  return `${format(new Date(datePart), 'dd.MM.yy')} ${timePart}`;
                }}
              />
              <text
                x={50}
                y={adjustedHeight / 2}
                textAnchor="middle"
                transform={`rotate(-90, 20, ${adjustedHeight / 2})`}
                style={{ fontSize: 10, fill: '#666' }}
              >
                [{axisUnit}]
              </text>
              <Bar dataKey="value" fill="#fcb268" fillOpacity={1} />
            </BarChart>
          </ChartContainer>
        </>
      ) : (
        <>
          <ChartHeader
            ranges={REGULAR_RANGE}
            selectedRange={selectedRange}
            onRangeChange={setSelectedRange}
            disabledRanges={REGULAR_RANGE.filter((range) => !availableRanges.includes(range))}
          />
          <ChartContainer height={adjustedHeight}>
            <BarChart data={data} height={adjustedHeight}>
              {isModalView && (
                <CartesianGrid
                  stroke="#aaa"
                  strokeDasharray="3 3"
                  vertical={true}
                  horizontal={true}
                  strokeOpacity={0.9}
                />
              )}
              <XAxis
                dataKey="date"
                tick={(props) => (
                  <CustomTick
                    {...props}
                    chartType="SpotMonthlyChart"
                    selectedRange={selectedRange}
                    TICK_GRANULARITY={TICK_GRANULARITY}
                  />
                )}
                tickMargin={10}
                interval={0}
                axisLine={true}
                tickLine={false}
                padding={{ right: 15 }}
              />
              <YAxis
                width={80}
                domain={[lowerLimit ?? 0, upperLimit ?? 'auto']}
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => formatValue(value)}
              />
              <Tooltip
                formatter={(value: any) => [`${formatValue(value)} ${tooltipUnit}`, 'Cena']}
                labelFormatter={(label) => `Data: ${format(new Date(label), 'dd.MM.yy')}`}
              />
              <text
                x={50}
                y={adjustedHeight / 2}
                textAnchor="middle"
                transform={`rotate(-90, 20, ${adjustedHeight / 2})`}
                style={{ fontSize: 10, fill: '#666' }}
              >
                [{axisUnit}]
              </text>
              <Bar dataKey="value" fill="#fcb268" fillOpacity={1} />
            </BarChart>
          </ChartContainer>
        </>
      )}
    </div>
  );
};

export default SpotMonthlyChart;
